import { useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { useI18n } from "../i18n/LanguageProvider";

const HashLink = ({ hash, children, className, onClick, testId }) => {
  const { lang } = useI18n();
  const navigate = useNavigate();
  return (
    <a
      href={`/${lang}#${hash}`}
      onClick={(e) => {
        e.preventDefault();
        if (onClick) onClick();
        const homePath = `/${lang}`;
        if (
          window.location.pathname === homePath ||
          window.location.pathname === `${homePath}/`
        ) {
          const el = document.getElementById(hash);
          if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
        } else {
          navigate(homePath, { state: { scrollToHash: hash } });
        }
      }}
      className={className}
      data-testid={testId}
    >
      {children}
    </a>
  );
};

export default function Header() {
  const { lang, t, switchLanguage } = useI18n();
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const isHome =
    location.pathname === `/${lang}` || location.pathname === `/${lang}/`;

  const close = () => setOpen(false);

  return (
    <header
      className="sticky top-0 z-50 bg-[#0F2537] border-b border-[#1a3550] text-white"
      data-testid="site-header"
    >
      <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-12">
        <div className="flex items-center justify-between h-16 lg:h-20">
          <Link
            to={`/${lang}`}
            className="flex items-center gap-3 group"
            data-testid="brand-logo-link"
            onClick={close}
          >
            <span className="inline-flex items-center justify-center w-9 h-9 rounded-sm bg-[#C85A32] text-white font-bold text-sm tracking-tight">
              Z
            </span>
            <span className="flex flex-col leading-none">
              <span className="font-heading text-lg font-semibold tracking-tight">
                {t("brand.name")}
              </span>
              <span className="text-[10px] tracking-widest uppercase text-slate-300 mt-0.5">
                {t("brand.tagline")}
              </span>
            </span>
          </Link>

          <nav
            className="hidden lg:flex items-center gap-8"
            data-testid="primary-nav"
          >
            <NavLink
              to={`/${lang}`}
              end
              className={({ isActive }) =>
                `text-sm transition-colors ${isActive ? "text-white" : "text-slate-200 hover:text-white"}`
              }
              data-testid="nav-home"
            >
              {t("nav.home")}
            </NavLink>
            <HashLink
              hash="sector"
              className="text-sm text-slate-200 hover:text-white transition-colors"
              testId="nav-sector"
            >
              {t("nav.sector")}
            </HashLink>
            <HashLink
              hash="process"
              className="text-sm text-slate-200 hover:text-white transition-colors"
              testId="nav-process"
            >
              {t("nav.process")}
            </HashLink>
            <HashLink
              hash="transparency"
              className="text-sm text-slate-200 hover:text-white transition-colors"
              testId="nav-transparency"
            >
              {t("nav.transparency")}
            </HashLink>
          </nav>

          <div className="hidden lg:flex items-center gap-4">
            <div
              className="flex items-center border border-[#2a4560] rounded-sm overflow-hidden"
              role="group"
              aria-label={t("nav.language")}
              data-testid="lang-switcher"
            >
              <button
                type="button"
                onClick={() => switchLanguage("ar")}
                className={`px-3 py-1.5 text-xs font-semibold tracking-widest uppercase ${
                  lang === "ar"
                    ? "bg-[#C85A32] text-white"
                    : "text-slate-200 hover:bg-[#12304a]"
                }`}
                aria-pressed={lang === "ar"}
                data-testid="lang-switch-ar"
              >
                ES
              </button>
              <button
                type="button"
                onClick={() => switchLanguage("en")}
                className={`px-3 py-1.5 text-xs font-semibold tracking-widest uppercase ${
                  lang === "en"
                    ? "bg-[#C85A32] text-white"
                    : "text-slate-200 hover:bg-[#12304a]"
                }`}
                aria-pressed={lang === "en"}
                data-testid="lang-switch-en"
              >
                EN
              </button>
            </div>
            <HashLink
              hash="newsletter"
              className="inline-flex items-center bg-[#C85A32] hover:bg-[#b04d28] text-white text-sm font-semibold px-4 py-2 rounded-sm"
              testId="nav-newsletter-cta"
            >
              {t("nav.newsletter")}
            </HashLink>
          </div>

          <button
            type="button"
            className="lg:hidden inline-flex items-center justify-center w-10 h-10 rounded-sm border border-[#2a4560] text-white"
            aria-label={open ? t("nav.menu_close") : t("nav.menu_open")}
            aria-expanded={open}
            onClick={() => setOpen((v) => !v)}
            data-testid="mobile-menu-toggle"
          >
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>

        {open && (
          <div
            className="lg:hidden border-t border-[#1a3550] py-4"
            data-testid="mobile-menu"
          >
            <nav className="flex flex-col gap-1">
              <NavLink
                to={`/${lang}`}
                end
                className="px-2 py-3 text-sm text-slate-100 border-b border-[#1a3550]"
                onClick={close}
                data-testid="mobile-nav-home"
              >
                {t("nav.home")}
              </NavLink>
              <HashLink
                hash="sector"
                className="px-2 py-3 text-sm text-slate-100 border-b border-[#1a3550]"
                onClick={close}
                testId="mobile-nav-sector"
              >
                {t("nav.sector")}
              </HashLink>
              <HashLink
                hash="process"
                className="px-2 py-3 text-sm text-slate-100 border-b border-[#1a3550]"
                onClick={close}
                testId="mobile-nav-process"
              >
                {t("nav.process")}
              </HashLink>
              <HashLink
                hash="transparency"
                className="px-2 py-3 text-sm text-slate-100 border-b border-[#1a3550]"
                onClick={close}
                testId="mobile-nav-transparency"
              >
                {t("nav.transparency")}
              </HashLink>
              <HashLink
                hash="newsletter"
                className="mt-4 inline-flex justify-center items-center bg-[#C85A32] hover:bg-[#b04d28] text-white text-sm font-semibold px-4 py-3 rounded-sm"
                onClick={close}
                testId="mobile-nav-newsletter-cta"
              >
                {t("nav.newsletter")}
              </HashLink>
              <div
                className="mt-4 flex items-center border border-[#2a4560] rounded-sm overflow-hidden self-start"
                role="group"
                aria-label={t("nav.language")}
                data-testid="mobile-lang-switcher"
              >
                <button
                  type="button"
                  onClick={() => {
                    switchLanguage("ar");
                    close();
                  }}
                  className={`px-4 py-2 text-xs font-semibold tracking-widest uppercase ${
                    lang === "ar"
                      ? "bg-[#C85A32] text-white"
                      : "text-slate-200"
                  }`}
                  data-testid="mobile-lang-switch-ar"
                >
                  ES
                </button>
                <button
                  type="button"
                  onClick={() => {
                    switchLanguage("en");
                    close();
                  }}
                  className={`px-4 py-2 text-xs font-semibold tracking-widest uppercase ${
                    lang === "en"
                      ? "bg-[#C85A32] text-white"
                      : "text-slate-200"
                  }`}
                  data-testid="mobile-lang-switch-en"
                >
                  EN
                </button>
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
}
